/**
 * Composes configurable table UX (column visibility, filters, sort persistence) with the shared {@link DataTable}.
 * Screens pass a column config plus row/header builders; visibility metadata can be surfaced for CSV export (e.g. Status logs download).
 */
import React, { useEffect, useMemo } from "react";
import DataTable from "./DataTable";
import TableControls from "./TableControls";
import { useConfiguredTable } from "./useConfiguredTable";

/**
 * @param {object} [dataTableProps] - Forwarded to {@link DataTable} (rowKey, maxHeight, minWidth, fullWidth, etc.).
 * @param {function(string): void} [onSortChange] - Optional; runs after sort. Feedback uses this to `setCurrentPage(0)` because sort reorders only the current in-memory slice.
 */
const ConfiguredDataTable = ({
  config,
  headers,
  rows,
  getCellDisplayValue,
  getCellSortValue,
  renderCell,
  onVisibleColumnsChange,
  onVisibleColumnLabelsChange,
  onVisibleColumnDefsChange,
  onFilteredRowCountChange,
  page = null,
  pageSize = null,
  defaultHeaderCellSx = {},
  showControls = true,
  controlsProps = {},
  dataTableProps = {},
  onSortChange,
}) => {
  const tableState = useConfiguredTable({
    config,
    headers,
    rows,
    getCellDisplayValue,
    getCellSortValue,
    onVisibleColumnsChange,
  });

  // Attach per-instance header styling and optional custom cell renderer (receives live tableState for dialogs/filters context).
  const tableColumns = useMemo(
    () =>
      tableState.visibleColumns.map((column) => ({
        ...column,
        headerCellSx: {
          ...defaultHeaderCellSx,
          ...column.headerCellSx,
        },
        render: (value, row) =>
          renderCell ? renderCell({ column, value, row, tableState }) : value,
      })),
    [defaultHeaderCellSx, renderCell, tableState]
  );

  // Omit the actions column from export metadata; parent uses labels/defs to align downloadable CSV with the grid.
  useEffect(() => {
    const visibleNoActions = tableState.visibleColumns.filter(
      (column) => column.key !== "actions"
    );
    onVisibleColumnLabelsChange?.(visibleNoActions.map((column) => column.label));
    onVisibleColumnDefsChange?.(
      visibleNoActions.map((column) => ({ key: column.key, label: column.label }))
    );
  }, [
    onVisibleColumnDefsChange,
    onVisibleColumnLabelsChange,
    tableState.visibleColumns,
  ]);

  const pagedRows = useMemo(() => {
    if (page == null || pageSize == null) return tableState.sortedRows;
    const start = page * pageSize;
    return tableState.sortedRows.slice(start, start + pageSize);
  }, [page, pageSize, tableState.sortedRows]);

  useEffect(() => {
    onFilteredRowCountChange?.(tableState.sortedRows.length);
  }, [onFilteredRowCountChange, tableState.sortedRows.length]);

  return (
    <React.Fragment>
      {showControls && (
        <TableControls
          allColumns={tableState.allColumns}
            visibleColumnKeys={tableState.visibleColumnKeys}
            defaultVisibleColumns={tableState.defaultVisibleColumns}
            onApplyVisibleColumns={tableState.applyVisibleColumns}
            filterableColumns={tableState.filterableColumns}
            filters={tableState.filters}
            onApplyFilters={tableState.setFilters}
            onClearFilters={tableState.clearFilters}
            {...controlsProps}
          />
      )}
      <DataTable
        columns={tableColumns}
        data={pagedRows}
        onSort={(columnKey) => {
          tableState.handleSort(columnKey);
          // Parent hooks (e.g. Feedback pagination) run after sort so behavior matches legacy DataTable usage.
          onSortChange?.(columnKey);
        }}
        orderBy={tableState.orderBy}
        order={tableState.order}
        {...dataTableProps}
      />
    </React.Fragment>
  );
};

export default ConfiguredDataTable;

